/**
 * Words and rules for the V1 payout boards (F4-09 Payouts console, F4-B4.a failed and returned,
 * F4-B4.b pay again, F4-B4.c fix bank details). Pure: the pages only render what these say.
 */
import type { ExceptionRow } from '../api/exceptions'
import type { BankAccount, PayoutRow, PayoutTry } from '../api/payouts'
import { BLOCKER_LABEL } from '../api/payouts'

export type Tone = 'wait' | 'bad' | 'ok' | 'idle' | 'help'

export const LIVE_STATUSES = new Set(['PENDING_APPROVAL', 'READY', 'SUBMITTING', 'PROCESSING'])
export const isFailed = (status: string) => status === 'FAILED' || status === 'REVERSED'

/** The board's Status · bank ref. pill for a payout row. */
export function payoutPill(p: Pick<PayoutRow, 'status' | 'requestedAt' | 'blockers' | 'mode'>, now = Date.now()): { tone: Tone; text: string } {
  switch (p.status) {
    case 'PENDING_APPROVAL': {
      if (p.blockers.some((b) => b.code !== 'PAPER_DAY_NOT_REVIEWED' && b.code !== 'PAPER_DAY')) return { tone: 'bad', text: 'Blocked' }
      const h = hoursSinceIso(p.requestedAt, now)
      return { tone: 'wait', text: h === null ? 'Waiting' : `Waiting ${h} h` }
    }
    case 'READY':
      return { tone: 'idle', text: 'Ready to send' }
    case 'SUBMITTING':
      return { tone: 'wait', text: p.mode === 'MANUAL' ? 'Waiting for a second person' : 'Sending' }
    case 'PROCESSING':
      return { tone: 'wait', text: 'With the bank' }
    case 'PROCESSED':
      return { tone: 'ok', text: 'Paid' }
    case 'FAILED':
      return { tone: 'bad', text: 'Failed' }
    case 'REVERSED':
      return { tone: 'bad', text: 'Returned' }
    case 'CANCELLED':
      return { tone: 'idle', text: 'Cancelled' }
    default:
      return { tone: 'idle', text: p.status }
  }
}

/** One blocker in the board's words ("Tag clash: tag 3117 is also on RMP-0371"). */
export function blockerText(b: { code: string; detail: Record<string, unknown> }): string {
  const d = b.detail
  switch (b.code) {
    case 'TAG_CONFLICT':
      return typeof d.tag === 'string' ? `Tag clash: tag ${d.tag} is on two bags` : 'Tag clash: the same tag is on two bags'
    case 'NO_VERIFIED_ACCOUNT':
      return 'No verified bank account: fix bank details'
    case 'PAPER_DAY_NOT_REVIEWED':
      return 'Paper day: approve after the page photo'
    default:
      return BLOCKER_LABEL[b.code] ?? humanizeCode(b.code)
  }
}

/** The board's "Reason or blocker" cell. */
export function reasonOrBlocker(p: Pick<PayoutRow, 'status' | 'blockers' | 'holdReason' | 'failureReason' | 'captureMode'>): string {
  if (isFailed(p.status)) {
    const r = p.failureReason ?? 'no reason from the bank'
    return failureIsAboutAccount(p.failureReason) ? `${capitalise(r)}: fix bank details` : capitalise(r)
  }
  if (p.status !== 'PENDING_APPROVAL') return ''
  if (p.blockers.length > 0) return p.blockers.map(blockerText).join(' · ')
  if (p.captureMode === 'PAPER' || (p.holdReason ?? '').includes('PAPER')) return 'Paper day: approve after the page photo'
  return p.holdReason ? humanizeCode(p.holdReason) : ''
}

/** Lot tag: Good / Good · paper / Red tie (· paper). */
export function lotTag(p: Pick<PayoutRow, 'stream' | 'captureMode'>): { tone: 'good' | 'paper' | 'bad'; text: string } {
  const paper = p.captureMode === 'PAPER'
  if (p.stream === 'CONV') return { tone: 'bad', text: paper ? 'Red tie · paper' : 'Red tie' }
  return paper ? { tone: 'paper', text: 'Good · paper' } : { tone: 'good', text: 'Good' }
}

/**
 * Is the bank's reason about his account (fix bank details first) or worth trying again? The
 * server's class (PAYMENT_FAILED detail.reason_class) wins; without it, words in the reason.
 * An unknown reason counts as an account problem until someone maps it (F4-B4.a).
 */
export function failureIsAboutAccount(reason: string | null | undefined, reasonClass?: string | null): boolean {
  if (reasonClass === 'ACCOUNT') return true
  if (reasonClass === 'RETRY') return false
  if (!reason) return true
  return !/offline|timeout|timed out|unavailable|down|try again|later|no_bank_reply|network|busy/i.test(reason)
}

export interface FailureItem {
  exceptionId: string | null
  unitId: string
  kind: string
  /** The latest failed or returned try of the lot. */
  payout: PayoutRow
  openedAt: string | null
  severity: string | null
  reasonClass: 'ACCOUNT' | 'RETRY'
  tries: number
}

/**
 * Open failures (F4-B4.a): one per lot and kind. The open PAYMENT_FAILED exception says when it
 * opened and how the server classed the reason; the latest failed try gives amount and account.
 * With the exceptions known, a failure is open while its PAYMENT_FAILED is OPEN (paying again
 * resolves it); without them (`null`), a failed try whose lot has a later live or paid try is not.
 */
export function openFailures(failed: PayoutRow[], exceptions: ExceptionRow[] | null, laterTries: PayoutRow[] = []): FailureItem[] {
  const latest = new Map<string, PayoutRow>()
  for (const p of failed) {
    const k = `${p.unitId}:${p.kind}`
    const cur = latest.get(k)
    if (!cur || p.attemptNo > cur.attemptNo) latest.set(k, p)
  }
  const superseded = (p: PayoutRow) =>
    laterTries.some((t) => t.unitId === p.unitId && t.kind === p.kind && t.attemptNo > p.attemptNo && !isFailed(t.status) && t.status !== 'CANCELLED')
  const byKey = new Map<string, ExceptionRow>()
  for (const e of exceptions ?? []) {
    const unit = typeof e.detail.unit_id === 'string' ? e.detail.unit_id : null
    const kind = typeof e.detail.kind === 'string' ? e.detail.kind : 'LOT'
    if (unit) byKey.set(`${unit}:${kind}`, e)
  }
  const out: FailureItem[] = []
  for (const [k, p] of latest) {
    const e = byKey.get(k) ?? null
    if (!e && (exceptions !== null || superseded(p))) continue
    const failures = e && Array.isArray(e.detail.failures) ? (e.detail.failures as Array<Record<string, unknown>>) : []
    const last = failures[failures.length - 1]
    const cls = (last?.reason_class ?? e?.detail.reason_class) as string | undefined
    out.push({
      exceptionId: e?.id ?? null,
      unitId: p.unitId,
      kind: p.kind,
      payout: p,
      openedAt: e?.raisedAt ?? p.processedAt ?? p.requestedAt,
      severity: e?.severity ?? null,
      reasonClass: failureIsAboutAccount(p.failureReason, cls) ? 'ACCOUNT' : 'RETRY',
      tries: Math.max(failures.length, p.attemptNo),
    })
  }
  return out.sort((a, b) => (a.openedAt ?? '').localeCompare(b.openedAt ?? ''))
}

/** The key the server gives the next try ({lot}-R{n}); shown before it is sent. */
export function nextTryKey(previousKey: string | null, nextAttempt: number): string | null {
  if (!previousKey || nextAttempt < 2) return null
  return `${previousKey.replace(/-R\d+$/, '')}-R${nextAttempt}`
}

/** A try that is waiting or in progress blocks another Pay (the server refuses it too). */
export function liveTry(tries: Pick<PayoutTry, 'status' | 'attemptNo'>[]): Pick<PayoutTry, 'status' | 'attemptNo'> | null {
  return tries.find((t) => LIVE_STATUSES.has(t.status)) ?? null
}

/**
 * The old and the new account on the fix-bank screen (F4-B4.c): the new one is a PENDING check,
 * else a VERIFIED primary that replaced a failed or switched-off one; the old one is the failed /
 * in-use one it replaces.
 */
export function pickAccounts(accounts: BankAccount[]): { old: BankAccount | null; fresh: BankAccount | null } {
  const byNewest = [...accounts].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
  const pending = byNewest.find((a) => a.status === 'PENDING') ?? null
  const failed = byNewest.find((a) => a.status === 'FAILED') ?? null
  const primary = byNewest.find((a) => a.isPrimary && a.status === 'VERIFIED') ?? null
  if (pending) return { fresh: pending, old: failed ?? primary ?? byNewest.find((a) => a !== pending) ?? null }
  if (primary) {
    const replaced = byNewest.find((a) => a !== primary && (a.status === 'FAILED' || a.status === 'DISABLED')) ?? null
    if (replaced) return { fresh: primary, old: replaced }
    return { fresh: null, old: primary }
  }
  return { fresh: null, old: failed ?? byNewest[0] ?? null }
}

/**
 * Two-person rule for Approve, in the board's words. The server is the authority (403); this only
 * says beforehand why the button is off. `lot` comes from GET /units/:id.
 */
export function approveOwnWork(
  p: Pick<PayoutRow, 'kind' | 'requestedById'>,
  lot: { committedById: string | null; sessionOperatorId: string | null; paperDay: boolean; typedById: string | null } | null,
  userId: string | undefined,
): { ok: boolean; text: string } {
  if (!userId) return { ok: true, text: '' }
  if (lot?.committedById === userId) return { ok: false, text: 'You closed this lot, so another person must approve it.' }
  if (lot?.paperDay && lot.typedById === userId) return { ok: false, text: 'You typed this lot, so another person must approve it.' }
  if (lot?.paperDay && lot.sessionOperatorId === userId) return { ok: false, text: 'You ran this paper day, so another person must approve it.' }
  if (p.kind === 'TOP_UP' && p.requestedById === userId) return { ok: false, text: 'You made the correction behind this top-up, so another person must approve it.' }
  return { ok: true, text: lot?.paperDay ? 'You did not type or close this lot, so you can approve it.' : 'You did not close this lot, so you can approve it.' }
}

export function hoursSinceIso(at: string | null | undefined, now = Date.now()): number | null {
  if (!at) return null
  return Math.max(0, Math.floor((now - new Date(at).getTime()) / 3_600_000))
}

export function humanizeCode(code: string): string {
  const s = code.replace(/[_-]+/g, ' ').toLowerCase().trim()
  return capitalise(s)
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export const TRY_ROUTE_LABEL: Record<string, string> = {
  MANUAL: 'Manual transfer',
  BULK_FILE: 'Bulk file',
  IMPS: 'Bank transfer',
  NEFT: 'Bank transfer',
  UPI: 'UPI',
}

const IST = 'Asia/Kolkata'
const parts = (at: string | number | Date, o: Intl.DateTimeFormatOptions) =>
  Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: IST, ...o }).formatToParts(new Date(at)).map((p) => [p.type, p.value]))

/** "Thu 2 Oct" for a YYYY-MM-DD day (IST) — the boards' day button. */
export function boardDay(day: string): string {
  const p = parts(`${day}T12:00:00+05:30`, { weekday: 'short', day: 'numeric', month: 'short' })
  return `${p.weekday} ${p.day} ${p.month}`
}

/** "1 Oct 09:30" (IST) — the boards' times. */
export function boardWhen(at: string | null | undefined): string {
  if (!at) return '—'
  const p = parts(at, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
  return `${p.day} ${p.month} ${p.hour}:${p.minute}`
}

/** "30 Sep" (IST). */
export function boardDate(at: string | null | undefined): string {
  if (!at) return '—'
  const p = parts(/^\d{4}-\d{2}-\d{2}$/.test(at) ? `${at}T12:00:00+05:30` : at, { day: 'numeric', month: 'short' })
  return `${p.day} ${p.month}`
}
